import React, { useRef, useState } from 'react';
import { isBrowser } from 'react-device-detect';
import { bindActionCreators } from 'redux';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';

import * as actions from 'containers/ProjectTools/Notes/actions';
import { useAutosizeTextarea } from 'hooks/useAutosizeTextarea';
import { MAX_NOTE_NAME_LENGTH } from 'utils/constants';
import LoadingIcon from 'assets/images/spinner.gif';
import clsx from 'clsx';
import { NoteTemplateOptionsButton } from './NoteTemplate.OptionsButton';
import './note-templates.scss';

const AutosizeTextarea = (props) => {
  const { textareaRef } = useAutosizeTextarea({ enterToSubmit: true });
  return (
    <textarea
      ref={textareaRef}
      maxLength={MAX_NOTE_NAME_LENGTH}
      {...props}
    />
  );
};

const NoteTemplate = ({
  id,
  title,
  name,
  url,
  isShortcut,
  imageUrls,
  image,
  originalImage,
  plannerOnly,
  clientReadOnly,
  setNodeRef,
  attributes,
  listeners,
  style,
}) => {
  const noteTemplateData = {
    id,
    title: name,
    url,
    plannerOnly,
    clientReadOnly,
    isShortcut,
    image,
  };

  const cardRef = useRef();
  const imageUrl = imageUrls?.rect260x175 ? imageUrls.rect260x175.url : null;
  const [editNoteTemplateTitle, setEditNoteTemplateTitle] = useState(false);
  const [noteTemplateTitle, setNoteTemplateTitle] = useState(name);
  const [isImageUpdating, setImageUpdating] = useState(false);

  const { orgId } = useParams();
  const dispatch = useDispatch();
  const { updateNoteTemplate } = bindActionCreators(actions, dispatch);

  const onChangeTitle = (e) => {
    if (e?.target?.value) {
      updateNoteTemplate({
        id: noteTemplateData.id,
        noteBody: { ...noteTemplateData, title: e.target.value, imageKey: image?.key },
      });
      setNoteTemplateTitle(e.target.value);
    }
    setEditNoteTemplateTitle(false);
  };

  const isInOptionMenu = (card, element) => {
    if (card === element || !element.parentElement) return false;
    if (element.classList.contains('ap-dropdown__content') ||
      element.classList.contains('ReactModal__Overlay') ||
      element.classList.contains('ap-dropdown__trigger') ||
      element.classList.contains('drawer-container')) {
        return true;
    }
    return isInOptionMenu(card, element.parentElement);
  };

  const dragListeners = !editNoteTemplateTitle && isBrowser ? listeners : null;
  const goToNoteTemplate = (noteId, templateUrl) => {
    if (templateUrl) {
      window.open(templateUrl, '_blank');
    } else {
      window.location = `/app/v2/pro/${orgId}/settings/templates/notes-templates/${noteId}`;
    }
  };

  return (
    <div
      key={id}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...dragListeners}
    >
      <div
        ref={cardRef}
        className="note-template-item"
        onClick={(e) => {
          if (editNoteTemplateTitle || isInOptionMenu(cardRef.current, e.target)) {
            return;
          }
          goToNoteTemplate(id, url);
        }}
      >
        <div className={clsx('note-template-item__avatar', !imageUrl && 'no-image')}>
          {isImageUpdating ? (
            <img
              src={LoadingIcon}
              alt="loading..."
              className="loader-icon"
            />
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={title}
              onDragStart={(e) => {
                e.preventDefault();
              }}
            />
          ) : null}
        </div>
        <div className="note-template-item__name">
          {editNoteTemplateTitle ? (
            <AutosizeTextarea
              className="ap-text note-item__name_update_grid unstyled"
              defaultValue={noteTemplateTitle.length <= MAX_NOTE_NAME_LENGTH
                ? noteTemplateTitle
                : `${noteTemplateTitle.slice(0, MAX_NOTE_NAME_LENGTH)}`}
              onBlur={onChangeTitle}
              autoFocus
              onFocus={(e) => {
                e.target.selectionStart = e.target.value.length;
              }}
            />
          ) : (
            <div className='note-template-item__name-text'>
              {noteTemplateTitle.length <= MAX_NOTE_NAME_LENGTH
                ? noteTemplateTitle
                : `${noteTemplateTitle.slice(0, MAX_NOTE_NAME_LENGTH)}...`}
              <button
                className='note-template-item__name-pencil button--unstyled'
                onClick={(e) => {
                  setEditNoteTemplateTitle(true);
                  e.stopPropagation();
                }}
              >
                <FontAwesomeIcon
                  icon={['fas', 'pencil']}
                />
              </button>
            </div>
          )}
        </div>
        {url && (
          <OpenInNewIcon className="note-template-item__external-link"/>
        )}
        <div className="note-item__options">
          <NoteTemplateOptionsButton
            imageUrls={imageUrls}
            image={image}
            originalImage={originalImage}
            id={id}
            url={url}
            noteTemplate={noteTemplateData}
            renameAction={()=> setEditNoteTemplateTitle(true)}
            setImageUpdating={setImageUpdating}
          />
        </div>
        <div className="note-item__dnd-icon">
          <ZoomOutMapIcon />
        </div>
      </div>
    </div>
  );
};

export default NoteTemplate;
